<template>
  <div class="questionnaire-list-container">
    <div class="header">
      <h2>{{ $t('questionnaire.title') }}</h2>
      <button 
        @click="fetchQuestionnaireList" 
        class="refresh-btn"
        :disabled="isLoading"
      >
        {{ isLoading ? $t('questionnaire.refreshing') : $t('questionnaire.refresh') }}
      </button>
    </div>

    <div v-if="isLoading" class="loading">
      {{ $t('questionnaire.loading') }}
    </div>
    
    <div v-else-if="questionnaireList && questionnaireList.length > 0" class="table-container">
      <table class="questionnaire-table">
        <thead>
          <tr>
            <th>{{ $t('questionnaire.table.index') }}</th>
            <th>{{ $t('questionnaire.table.createDate') }}</th>
            <th>{{ $t('questionnaire.table.satisfaction') }}</th>
            <th>{{ $t('questionnaire.table.name') }}</th>
            <th>{{ $t('questionnaire.table.email') }}</th>
            <th>{{ $t('questionnaire.table.telephone') }}</th>
            <th>{{ $t('questionnaire.table.details') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in questionnaireList" :key="item.i_index" class="table-row">
            <td>{{ item.i_index }}</td>
            <td>{{ formatDateTime(item.createDate) }}</td>
            <td>
              <span class="satisfaction-badge">{{ item.satisfaction }}/5</span>
            </td>
            <td>{{ item.name || 'N/A' }}</td>
            <td>{{ item.email || 'N/A' }}</td>
            <td>{{ item.telephone || 'N/A' }}</td>
            <td>
              <button 
                @click="showDetails(item)" 
                class="details-btn"
              >
                {{ $t('questionnaire.viewDetails') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div v-else class="no-data">
      {{ $t('questionnaire.noRecords') }}
    </div>

    <!-- Details Modal -->
    <div v-if="selectedItem" class="modal-overlay" @click="selectedItem = null">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('questionnaire.detailsTitle') }} - {{ $t('questionnaire.table.index') }}: {{ selectedItem.i_index }}</h3>
          <button @click="selectedItem = null" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="detail-grid">
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.createDate') }}</label>
              <p>{{ formatDateTime(selectedItem.createDate) }}</p>
            </div>
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.satisfaction') }}</label>
              <p>{{ selectedItem.satisfaction }}/5</p>
            </div>
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.feature') }}</label>
              <p>
                {{ getFeatureLabel(selectedItem.feature) }}
                <span v-if="selectedItem.featureOther" class="other-text">({{ selectedItem.featureOther }})</span>
              </p>
            </div>
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.purpose') }}</label>
              <p>
                {{ getPurposeLabel(selectedItem.purpose) }}
                <span v-if="selectedItem.purposeOther" class="other-text">({{ selectedItem.purposeOther }})</span>
              </p>
            </div>
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.device') }}</label>
              <p>
                {{ getDeviceLabel(selectedItem.device) }}
                <span v-if="selectedItem.deviceOther" class="other-text">({{ selectedItem.deviceOther }})</span>
              </p>
            </div>
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.name') }}</label>
              <p>{{ selectedItem.name || 'N/A' }}</p>
            </div>
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.email') }}</label>
              <p>{{ selectedItem.email || 'N/A' }}</p>
            </div>
            <div class="detail-item">
              <label>{{ $t('questionnaire.table.telephone') }}</label>
              <p>{{ selectedItem.telephone || 'N/A' }}</p>
            </div>
            <div class="detail-item full-width">
              <label>{{ $t('questionnaire.table.suggestion') }}</label>
              <p class="suggestion-text">{{ selectedItem.suggestion || 'N/A' }}</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="selectedItem = null" class="close-button">
            {{ $t('questionnaire.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'QuestionnaireList',
  data() {
    return {
      questionnaireList: [],
      selectedItem: null,
      isLoading: false
    }
  },
  mounted() {
    this.fetchQuestionnaireList()
  },
  methods: {
    formatDateTime(isoString) {
      if (!isoString) return 'N/A'
      try {
        const date = new Date(isoString)
        return date.toLocaleString()
      } catch (e) {
        console.error("Error formatting date:", isoString, e)
        return isoString
      }
    },
    getFeatureLabel(value) {
      const features = {
        '1': this.$t('questionnaire.features.option1'),
        '2': this.$t('questionnaire.features.option2'),
        '3': this.$t('questionnaire.features.option3'),
        '4': this.$t('questionnaire.features.option4'),
        '5': this.$t('questionnaire.features.option5')
      }
      return features[value] || value || 'N/A'
    },
    getPurposeLabel(value) {
      const purposes = {
        '1': this.$t('questionnaire.purposes.option1'),
        '2': this.$t('questionnaire.purposes.option2'),
        '3': this.$t('questionnaire.purposes.option3'),
        '4': this.$t('questionnaire.purposes.option4'),
        '5': this.$t('questionnaire.purposes.option5')
      }
      return purposes[value] || value || 'N/A'
    },
    getDeviceLabel(value) {
      const devices = {
        '1': this.$t('questionnaire.devices.option1'),
        '2': this.$t('questionnaire.devices.option2'),
        '3': this.$t('questionnaire.devices.option3'),
        '4': this.$t('questionnaire.devices.option4'),
        '5': this.$t('questionnaire.devices.option5')
      }
      return devices[value] || value || 'N/A'
    },
    async fetchQuestionnaireList() {
      this.isLoading = true
      try {
        // TODO: Update this endpoint based on your actual API
        const response = await axios.post('/Questionnaires/List')
        
        if (response.data && response.data.rtnCode === '00') {
          this.questionnaireList = response.data.questionnaireList || response.data || []
        } else if (Array.isArray(response.data)) {
          this.questionnaireList = response.data
        } else {
          throw new Error(response.data?.errMsg || this.$t('questionnaire.invalidResponse'))
        }
      } catch (error) {
        console.error('Error fetching questionnaire list:', error)
        this.questionnaireList = []
        alert(this.$t('questionnaire.fetchError'))
      } finally {
        this.isLoading = false
      }
    },
    showDetails(item) {
      this.selectedItem = item
    }
  }
}
</script>

<style scoped>
.questionnaire-list-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
}

.header h2 {
  margin: 0;
  color: #333;
}

.refresh-btn {
  padding: 10px 20px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #1565c0;
}

.refresh-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.loading, .no-data {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
}

.table-container {
  overflow-x: auto;
}

.questionnaire-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.questionnaire-table thead {
  background-color: #f5f5f5;
}

.questionnaire-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #ddd;
}

.questionnaire-table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.table-row:hover {
  background-color: #f9f9f9;
}

.satisfaction-badge {
  display: inline-block;
  padding: 4px 8px;
  background-color: #ffc107;
  color: #333;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
}

.details-btn {
  padding: 6px 12px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.3s;
}

.details-btn:hover {
  background-color: #1565c0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-weight: 600;
  color: #666;
  margin-bottom: 5px;
  font-size: 14px;
}

.detail-item p {
  margin: 0;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
  color: #333;
}

.other-text {
  color: #666;
  font-style: italic;
}

.suggestion-text {
  min-height: 60px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.close-button {
  padding: 10px 20px;
  background-color: #666;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.close-button:hover {
  background-color: #555;
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
  
  .questionnaire-table {
    font-size: 12px;
  }
  
  .questionnaire-table th,
  .questionnaire-table td {
    padding: 8px;
  }
}
</style>

